import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CrosshairMode, LineStyle, CandlestickSeriesOptions, CandlestickSeries, LineSeries, HistogramSeriesOptions, HistogramSeries, Time, AreaSeries,  } from 'lightweight-charts';
import { Card, CardContent, Typography, Box, Button } from '@mui/material';
import { HistoricalData } from '../../types/HistoricalData';
import { BorderVertical } from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';

interface Props {
  data: HistoricalData[];
  height?: number;
  width?: number;
  onTimeRangeChange?: (range: '5M' | '10M') => void;
  isTrimmed?: boolean;
}

interface StockChartData extends Omit<HistoricalData, 'date'> {
  date: Date;
  open: number;
  close: number;
  low: number;
  high: number;
  volume: number;
  EMA_20: number;
  EMA_50: number;
  avgProjectedClose?: number | null;
  avgProjectedCloseLower?: number | null;
  avgProjectedCloseUpper?: number | null;
  projected_close_1week?: number;
  projected_close_1week_lowerbound?: number;
  projected_close_1week_upperbound?: number;
  projected_close_2weeks?: number;
  projected_close_2weeks_lowerbound?: number;
  projected_close_2weeks_upperbound?: number;
  projected_close_1month?: number;
  projected_close_1month_lowerbound?: number;
  projected_close_1month_upperbound?: number;
  shortTermRatingColor?: string;
  swingTradeRatingColor?: string;
  longTermRatingColor?: string;
  ShortTermRating?: string;
  SwingTradeRating?: string;
  LongTermRating?: string;
}

const ratingColorMap: { [key: string]: string } = {
  'strong buy': '#26a69a', // Green
  'buy': '#66bb6a',       // Light Green
  'hold': '#ffca28',      // Yellow
  'sell': '#ef5350',      // Red
  'strong sell': '#d32f2f',  // Dark Red
};

const processData = (data: HistoricalData[], isTrimmed: boolean, timeRange: string): StockChartData[] => {
  const sortedData = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const processedData: StockChartData[] = sortedData.map(item => ({
    ...item,
    date: new Date(`${item.date}T12:00:00`),
    open: Number(item.open) || 0,
    close: Number(item.close) || 0,
    low: Number(item.low) || 0,
    high: Number(item.high) || 0,
    volume: Number(item.volume) || 0,
    EMA_20: Number(item.EMA_20) || 0,
    EMA_50: Number(item.EMA_50) || 0,
    projected_close_1week: Number(item.projected_close_1week) || 0,
    projected_close_1week_lowerbound: Number(item.projected_close_1week_lowerbound) || 0,
    projected_close_1week_upperbound: Number(item.projected_close_1week_upperbound) || 0,
    projected_close_2weeks: Number(item.projected_close_2weeks) || 0,
    projected_close_2weeks_lowerbound: Number(item.projected_close_2weeks_lowerbound) || 0,
    projected_close_2weeks_upperbound: Number(item.projected_close_2weeks_upperbound) || 0,
    projected_close_1month: Number(item.projected_close_1month) || 0,
    projected_close_1month_lowerbound: Number(item.projected_close_1month_lowerbound) || 0,
    projected_close_1month_upperbound: Number(item.projected_close_1month_upperbound) || 0,
    shortTermRatingColor: item.ShortTermRating ? ratingColorMap[item.ShortTermRating.toLowerCase() || 'hold'] : undefined,
    swingTradeRatingColor: item.SwingTradeRating ? ratingColorMap[item.SwingTradeRating.toLowerCase() || 'hold'] : undefined,
    longTermRatingColor: item.LongTermRating ? ratingColorMap[item.LongTermRating.toLowerCase() || 'hold'] : undefined,
  }));

  // Extend data into the future by 20 business days
  const futureExtension: StockChartData[] = [];
  let lastDate = processedData[processedData.length - 1].date;
  // console.log('Last Date');
  // console.log(lastDate);
  // console.log(sortedData[sortedData.length - 1].date);
  // console.log(new Date(sortedData[sortedData.length - 1].date));
  let businessDaysAdded = 0;

  while (businessDaysAdded < 20) {
    const nextDate = new Date(lastDate);
    nextDate.setDate(lastDate.getDate() + 1);
    const dayOfWeek = nextDate.getDay();

    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      futureExtension.push({
        date: nextDate,
      } as StockChartData);
      businessDaysAdded++;
    }
    lastDate = nextDate;
  }

  const extendedData = [...processedData, ...futureExtension];

  // Fix the averaging: use i-5, i-10, i-20 instead of walking forward
  for (let i = 0; i < extendedData.length; i++) {
    let total = 0, totalLower = 0, totalUpper = 0, count = 0;
    if (i - 5 >= 0 && extendedData[i - 5].projected_close_1week) {
      if (extendedData[i - 5]) {
        total += extendedData[i - 5].projected_close_1week || 0;
        totalLower += extendedData[i - 5].projected_close_1week_lowerbound || 0;
        totalUpper += extendedData[i - 5].projected_close_1week_upperbound || 0;
      }
      count++;
    }
    if (i - 10 >= 0 && extendedData[i - 10].projected_close_2weeks) {
      if (extendedData[i - 10]) {
        total += extendedData[i - 10].projected_close_2weeks || 0;
        totalLower += extendedData[i - 10].projected_close_2weeks_lowerbound || 0;
        totalUpper += extendedData[i - 10].projected_close_2weeks_upperbound || 0;
      }
      count++;
    }
    if (i - 20 >= 0 && extendedData[i - 20].projected_close_1month) {
      total += extendedData[i - 20].projected_close_1month || 0;
      totalLower += extendedData[i - 20].projected_close_1month_lowerbound || 0;
      totalUpper += extendedData[i - 20].projected_close_1month_upperbound || 0;
      count++;
    }
    if (count > 0) {
      extendedData[i].avgProjectedClose = total / count; // reuse existing field or rename
      extendedData[i].avgProjectedCloseLower = totalLower / count;
      extendedData[i].avgProjectedCloseUpper = totalUpper / count;
    } 
  }

  return isTrimmed ? extendedData.slice(-70) : extendedData.slice(timeRange === '5M' ? -110 : -220);
};

export const StockChart: React.FC<Props> = ({ data, height = 600, width = 1200, isTrimmed = false, onTimeRangeChange }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<'5M' | '10M'>('5M');
  const [clickedIndex, setClickedIndex] = useState<number | null>(null);
  const processedData = processData(data, isTrimmed, timeRange);

  // Define chart colors based on theme
  const chartColors = {
    upColor: theme.palette.success.main,
    downColor: theme.palette.error.main,
    volumeUp: alpha(theme.palette.success.main, 0.3),
    volumeDown: alpha(theme.palette.error.main, 0.3),
    gridLines: theme.palette.mode === 'dark' ? 
      alpha(theme.palette.common.white, 0.1) : 
      alpha(theme.palette.common.black, 0.1),
    chartBackground: theme.palette.background.paper
  };

  useEffect(() => {
    if (onTimeRangeChange) {
      onTimeRangeChange(timeRange);
    }
  }, [timeRange, onTimeRangeChange]);

  // const chartData = isTrimmed ? processedData : processedData.slice(timeRange === '5M' ? -70 : -120);

  useEffect(() => {
    if (chartContainerRef.current) {
      const chart = createChart(chartContainerRef.current, {
        // width: width,
        height: height,
        crosshair: {
          mode: isTrimmed ? CrosshairMode.Hidden : CrosshairMode.Normal,
        },
        layout: {
          background: { color: chartColors.chartBackground },
          textColor: theme.palette.text.primary,
        },
        grid: {
          vertLines: { color: chartColors.gridLines },
          horzLines: { color: chartColors.gridLines },
        },
        rightPriceScale: {
          borderColor: chartColors.gridLines,
        },
        timeScale: {
          borderColor: chartColors.gridLines,
          timeVisible: true,
        },
      });

      if (isTrimmed) {
        chart.applyOptions({
          handleScale: false,
          rightPriceScale: {
            visible: true,
            borderVisible: false,
          },
          timeScale: {
            visible: true,
            borderVisible: false,
          }
        });
      } else {
        chart.applyOptions({
          handleScale: false,
          rightPriceScale: {
            scaleMargins: {
              top: 0.1,
              bottom: 0.2,
            },
            textColor: theme.palette.text.primary,
            borderColor: chartColors.gridLines,
            entireTextOnly: true,
            ticksVisible: false,
            mode: 1,
          },
          timeScale: {
            visible: true,
            borderVisible: false,
            // rightOffset: 12,  // Add some padding on the right side
            // barSpacing: 10,   // Ensure adequate spacing between bars
          },
        });
        chart.priceScale('left').applyOptions({
          scaleMargins: {
            top: 0.2,
            bottom: 0.18,
          },
        })
      }

      // Candlestick Series
      const candlestickSeries = chart.addSeries(CandlestickSeries, {
        upColor: chartColors.upColor,
        borderUpColor: chartColors.upColor,
        wickUpColor: chartColors.upColor,
        downColor: chartColors.downColor,
        borderDownColor: chartColors.downColor,
        wickDownColor: chartColors.downColor,
        borderVisible: true,
        wickVisible: true,
        borderColor: chartColors.gridLines,
        wickColor: chartColors.gridLines,
      } as CandlestickSeriesOptions);

      candlestickSeries.setData(
        processedData.map(item => ({
          time: { year: item.date.getFullYear(), month: item.date.getMonth() + 1, day: item.date.getDate() },
          open: item.open,
          high: item.high,
          low: item.low,
          close: item.close,
        }))
      );

      // Volume Series
      const volumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: '',
      } as HistogramSeriesOptions);

      volumeSeries.priceScale().applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0,
        },
      });

      volumeSeries.setData(
        processedData.map(item => ({
          time: { year: item.date.getFullYear(), month: item.date.getMonth() + 1, day: item.date.getDate() },
          value: item.volume,
          color: item.close >= item.open ? chartColors.volumeUp : chartColors.volumeDown,
        }))
      );

      // // EMA_20 Series
      // const ema20Series = chart.addSeries(LineSeries, {
      //   color: 'orange',
      //   lineWidth: 1,
      // });

      // if (!isTrimmed) {
      //   const ema20Data = processedData
      //     .filter(item => item.EMA_20 !== undefined)
      //     .map(item => ({
      //       time: { year: item.date.getFullYear(), month: item.date.getMonth() + 1, day: item.date.getDate() },
      //       value: item.EMA_20,
      //     }));

      //   ema20Series.setData(ema20Data);

      //   // EMA_50 Series
      //   const ema50Series = chart.addSeries(LineSeries, {
      //     color: 'purple',
      //     lineWidth: 1,
      //   });

      //   const ema50Data = processedData
      //     .filter(item => item.EMA_50 !== undefined)
      //     .map(item => ({
      //       time: { year: item.date.getFullYear(), month: item.date.getMonth() + 1, day: item.date.getDate() },
      //       value: item.EMA_50,
      //     }));

      //   ema50Series.setData(ema50Data);
      // }

      // Projected Close Series
      // const projectedCloseSeries = chart.addSeries(LineSeries, {
      //   color: 'blue',
      //   lineWidth: 1,
      //   lineStyle: LineStyle.Dotted,
      // });

      // const projectedCloseData = processedData
      //   .filter(item => item.avgProjectedClose !== undefined)
      //   .map(item => ({
      //     time: { year: item.date.getFullYear(), month: item.date.getMonth() + 1, day: item.date.getDate() },
      //     value: item.avgProjectedClose || 0,
      //   }));

      // projectedCloseSeries.setData(projectedCloseData);

      chart.subscribeClick(param => {
        if (!param.point || !param.time) return;
        
        const time = param.time as Time;
        const year = time.year;
        const month = time.month;
        const day = time.day;
        
        // Find the corresponding data index
        const cIndex = processedData.findIndex(item => 
          item.date.getFullYear() === year &&
          item.date.getMonth() + 1 === month &&
          item.date.getDate() === day
        );
        
        if (cIndex !== -1) {
          console.log(`Clicked on index ${cIndex}, date: ${processedData[cIndex].date}`);
          // You can now use this index for any operation
          // For example, call a callback function with the index
          setClickedIndex(cIndex);
        }
      });

      let k = clickedIndex !== null && clickedIndex <= processedData.length - 21 ? clickedIndex : processedData.length - 21;
      for (let i = k - 10; i <= k; i++) {
        if (i < 0 || i >= processedData.length) continue; // Skip out of bounds
        const projectedForDate = chart.addSeries(LineSeries, {
          color: i == k ? 
            theme.palette.error.main : // Current selected (red)
            alpha(theme.palette.mode === 'dark' ? 
              theme.palette.grey[500] : 
              theme.palette.grey[700], 
              0.01 + 0.3*i/k),
          lineWidth: 1,
          lastValueVisible: false,
          priceLineVisible: false,
        });
        
        const projectedForDateData = [{
          date: processedData[i].date,
          value: processedData[i].close
        },
        {
          date: processedData[i+5]?.date,
          value: processedData[i].projected_close_1week
        },
        {
          date: processedData[i+10]?.date,
          value: processedData[i].projected_close_2weeks
        },
        {
          date: processedData[i+20]?.date,
          value: processedData[i].projected_close_1month
        }].map(item => ({
          time: { year: item.date?.getFullYear(), month: item.date?.getMonth() + 1, day: item.date?.getDate() },
          value: item.value || 0,
        }));

        projectedForDate.setData(projectedForDateData);
      }

      if (!isTrimmed) {
          // Projected Close Bands
        //   const upperBandSeries = chart.addSeries(LineSeries, {
        //     color: 'rgba(0, 0, 255, 0.3)',
        //     lineWidth: 1,
        //     lineStyle: LineStyle.Dotted,
        //     lastValueVisible: false,
        //     priceLineVisible: false,
        // });

        // const upperBandData = processedData
        //   .filter(item => item.avgProjectedCloseUpper !== undefined)
        //   .map(item => ({
        //     time: { year: item.date.getFullYear(), month: item.date.getMonth() + 1, day: item.date.getDate() },
        //     value: item.avgProjectedCloseUpper || 0,
        //   }));

        // upperBandSeries.setData(upperBandData);

        // const lowerBandSeries = chart.addSeries(LineSeries, {
        //   color: 'rgba(0, 0, 255, 0.3)',
        //   lineWidth: 1,
        //   lineStyle: LineStyle.Dotted,
        //   lastValueVisible: false,
        //   priceLineVisible: false,
        // });

        // const lowerBandData = processedData
        //   .filter(item => item.avgProjectedCloseLower !== undefined)
        //   .map(item => ({
        //     time: { year: item.date.getFullYear(), month: item.date.getMonth() + 1, day: item.date.getDate() },
        //     value: item.avgProjectedCloseLower || 0,
        //   }));

        // lowerBandSeries.setData(lowerBandData);
      }



      // Tooltip
      interface Time {
        year: number;
        month: number;
        day: number;
      }

      if (!isTrimmed) {
      
          const renderRatingLine = (ratingType: 'shortTermRatingColor' | 'swingTradeRatingColor' | 'longTermRatingColor', 
                                    colorOffset: number) => {
            const ratingSeries = chart.addSeries(AreaSeries, {
              // color: 'transparent',
              lineWidth: 1,
              lastValueVisible: false,
              priceLineVisible: false,
              priceScaleId: 'left',
              title: ratingType.slice(0,2) === 'sh' ? 'Short Term' : ratingType.slice(0,2) === 'sw' ? 'Mid Term' : 'Long Term',
              // autoscaleInfoProvider: () => ({
              //   priceRange: {
              //       minValue: 0,
              //       maxValue: 30,
              //   },
              // }),
            },
            colorOffset
          );
    
            ratingSeries.setData(
              processedData.filter(item => item[ratingType]).map(item => ({
                time: { year: item.date.getFullYear(), month: item.date.getMonth() + 1, day: item.date.getDate() },
                // value: colorOffset, // Adjust position above candlesticks
                value: item.close, // Adjust position above candlesticks
                lineColor: item[ratingType],
                topColor: item[ratingType],
              }))
            );
          };
    
          renderRatingLine('shortTermRatingColor', 1);
          renderRatingLine('swingTradeRatingColor', 3);
          renderRatingLine('longTermRatingColor', 5);
      }
      chart.subscribeCrosshairMove(param => {
        if (!param.time || !tooltipRef.current) {
          return;
        }

        tooltipRef.current.style.display = 'block';

        const time = param.time as Time;
        const year = time.year;
        const month = time.month;
        const day = time.day;

        const dateStr = `${year}-${month}-${day}`;

        // Get data for the current date
        const dataItem = processedData.find(item =>
          item.date.getFullYear() === year &&
          item.date.getMonth() + 1 === month &&
          item.date.getDate() === day
        );

        if (dataItem) {
          tooltipRef.current.innerHTML = `
            <div style="font-weight: bold; margin-bottom: 4px;">Date: ${month}/${day}/${year}</div>
            ${dataItem.close ? "<div><b>Close:</b> $" + dataItem.close.toFixed(2) + "</div>" : ""}
            ${dataItem.avgProjectedClose ? "<div><b>Projected Price:</b> $" + dataItem.avgProjectedClose.toFixed(2) + "</div>" : ""}
            <div style="margin-top: 6px; font-weight: bold; color: #666;">Price Signals:</div>
            ${dataItem.ShortTermRating ? "<div><b>Short Term:</b> <span style='color:" + dataItem.shortTermRatingColor + "'>" + dataItem.ShortTermRating + "</span></div>" : ""}
            ${dataItem.SwingTradeRating ? "<div><b>Mid Term:</b> <span style='color:" + dataItem.swingTradeRatingColor + "'>" + dataItem.SwingTradeRating + "</span></div>" : ""}
            ${dataItem.LongTermRating ? "<div><b>Long Term:</b> <span style='color:" + dataItem.longTermRatingColor + "'>" + dataItem.LongTermRating + "</span></div>" : ""}
          `;

          if (param.point) {
            const yCoordinate = param.point.y;
            tooltipRef.current.style.top = yCoordinate + 'px';
            tooltipRef.current.style.left = param.point.x + 'px';
            if (chartContainerRef.current) {
              const x = param.point.x;
              const y = param.point.y;

              // Tooltip offset
              const offsetX = 20;
              const offsetY = 10;

              // Get chart container dimensions
              const chartWidth = chartContainerRef.current.offsetWidth;
              const chartHeight = chartContainerRef.current.offsetHeight;

              // Get tooltip dimensions (we need to make sure the tooltip is rendered to get the dimensions)
              tooltipRef.current.style.position = 'absolute';
              tooltipRef.current.style.visibility = 'hidden';
              tooltipRef.current.style.display = 'block';
              const tooltipWidth = tooltipRef.current.offsetWidth;
              const tooltipHeight = tooltipRef.current.offsetHeight;
              // tooltipRef.current.style.display = 'none';
              tooltipRef.current.style.visibility = 'visible';
              // tooltipRef.current.style.position = 'absolute';

              // Calculate tooltip position, ensuring it stays within the chart bounds
              let tooltipX = x + offsetX;
              let tooltipY = y + offsetY;

              // Adjust position if tooltip overflows on the right
              if (tooltipX + tooltipWidth > chartWidth) {
                tooltipX = x - tooltipWidth - offsetX;
              }

              // Adjust position if tooltip overflows on the bottom
              if (tooltipY + tooltipHeight > chartHeight) {
                tooltipY = y - tooltipHeight - offsetY;
              }

              tooltipRef.current.style.left = tooltipX + 'px';
              tooltipRef.current.style.top = tooltipY + 'px';
            }
          }          
        } else {
          tooltipRef.current.innerHTML = `No data for ${dateStr}`;
        }
      });

      chartContainerRef.current.addEventListener('mouseleave', () => {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      });
      // }


      // Automatically scale the chart to the timeframe included in the data
      chart.timeScale().fitContent();

      return () => {
        chart.remove();
      };
    }
  }, [processedData, height, width, isTrimmed, theme, timeRange]);

  const tooltipStyle = {
    // position: 'absolute',
    display: 'none',
    padding: theme.spacing(1.5), // Increased padding
    border: `1px solid ${theme.palette.divider}`, // Use theme border color
    backgroundColor: alpha(theme.palette.background.paper, 0.95), // Semi-transparent background
    color: theme.palette.text.primary, // Use theme text color
    zIndex: 10,
    borderRadius: theme.shape.borderRadius, // Use theme border radius
    boxShadow: theme.shadows[3], // Increased shadow for better visibility
    fontSize: theme.typography.body2.fontSize, // Use theme font size
    fontFamily: theme.typography.fontFamily, // Use theme font family
    minWidth: '180px', // Ensure tooltip has minimum width
    maxWidth: '250px', // Limit maximum width
  };

  return (
    <Card sx={{ margin: 'auto', mt: 4, boxShadow: 3 }}>
      <CardContent sx={{}}>
        {!isTrimmed && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              variant={timeRange === '5M' ? 'contained' : 'outlined'}
              onClick={() => setTimeRange('5M')}
              size="small"
              sx={{ mr: 1 }}
            >
              5M
            </Button>
            <Button
              variant={timeRange === '10M' ? 'contained' : 'outlined'}
              onClick={() => setTimeRange('10M')}
              size="small"
            >
              10M
            </Button>
          </Box>
        )}
        <div ref={chartContainerRef} style={{position: 'relative'}}>
          {(<div
            ref={tooltipRef}
            style={tooltipStyle}
            // style={{
            //   position: 'absolute',
            //   display: 'none',
            //   padding: '10px',
            //   border: '1px solid #ccc',
            //   background: 'white',
            //   zIndex: 10,
            // }}
          />)}
        </div>
      </CardContent>
    </Card>
  );
};